import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Box, Button } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'

import { getContractVersionSchedule } from 'modules/contracts/lib/contractSchedule'
import { contractVersionEmployee } from 'modules/contracts/lib/contractVersionEmployee'
import type ContractVersion from 'modules/contracts/store/models/ContractVersion'
import type ContractVersionRequest from 'modules/contracts/store/models/ContractVersionRequest'
import employees from 'modules/employees/store/collections/employees'
import policyAuthorizations from 'modules/permissions/store/collections/policyAuthorizations'

interface Props {
  selectedContractVersion: ContractVersion
  allContractVersionsSorted: ContractVersion[]
  onClickFix: () => void
  onClickDelete: () => void
  contractVersionRequest?: ContractVersionRequest | undefined
}

const Menu = ({
  selectedContractVersion,
  allContractVersionsSorted,
  onClickFix,
  onClickDelete,
  contractVersionRequest,
}: Props) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const contractVersionSchedule = getContractVersionSchedule(
    selectedContractVersion,
    allContractVersionsSorted
  )
  const [showDeleteModal, setShowDeleteModal] = React.useState(false)
  const employee = contractVersionEmployee(selectedContractVersion, employees)!
  const canEditContracts = policyAuthorizations.for('contracts.edit', employee)
  const canDeleteContracts = policyAuthorizations.for('contracts.delete_contracts', employee)

  const onDelete = () => {
    // If a contract version is pending, both delete and fix perform the same action,
    // blocking any changes to the contract versions until the contract
    // change is approved or rejected.

    asyncAction(async () => {
      await selectedContractVersion.delete()
      navigate(
        navigation.employees.id.contractVersions.fullPath({
          id: employee.id,
        })
      )
    })
    track(trackingEvents.employee.contracts.deleteContractConditions, {
      contract_status: contractVersionSchedule,
    })
  }

  const onCancelDelete = () => {
    setShowDeleteModal(false)
  }

  const onConfirmClickDelete = () => {
    setShowDeleteModal(false)
    onDelete()
  }

  const onDeleteClick = () => {
    if (!contractVersionRequest) {
      setShowDeleteModal(true)
    }
    onClickDelete()
  }

  const onFix = () => {
    track(trackingEvents.employee.contracts.fixContractStart, {
      contract_status: contractVersionSchedule,
    })
    onClickFix()
  }

  return (
    <>
      <Box role="menu">
        <PopoverMenu type="list">
          <PopoverMenu.Trigger>
            <Button.Decorative type="secondary" iconName="ellipsis" />
          </PopoverMenu.Trigger>
          <PopoverMenu.Content side="bottom" align="end">
            {canEditContracts && (
              <Box className="employee_profile_contracts_tab_fix_conditions_button">
                <PopoverMenu.TouchableOption
                  title={i18n.t('contract_versions.header.fix_conditions')}
                  onClick={() => onFix()}
                />
              </Box>
            )}
            {canDeleteContracts && (
              <PopoverMenu.TouchableOption
                title={i18n.t('contract_versions.navigator.cards.delete_version')}
                onClick={() => onDeleteClick()}
              />
            )}
          </PopoverMenu.Content>
        </PopoverMenu>
      </Box>
      {showDeleteModal && (
        <Modal
          title={i18n.t('contract_versions.modals.delete.title')}
          description={i18n.t('contract_versions.modals.delete.description')}
          illustration="delete"
          onClose={() => onCancelDelete()}
          onSubmit={() => onConfirmClickDelete()}
          footer={
            <>
              <Button type="secondary" onClick={() => onCancelDelete()}>
                {i18n.t('contract_versions.modals.delete.cancel')}
              </Button>
              <Button type="negative" submit>
                {i18n.t('contract_versions.modals.delete.delete')}
              </Button>
            </>
          }
        />
      )}
    </>
  )
}

export default observer(Menu)
